import { useRef, useState } from 'react'
import { toast } from 'sonner'
import { authApi } from '@/api/auth'
import { Button } from '@/components/ui/button'
import { Upload, X, Loader2 } from 'lucide-react'

interface ImageUploadFieldProps {
	value: string
	onChange: (url: string) => void
	placeholder?: string
}

export function ImageUploadField({ value, onChange, placeholder = '点击上传图片' }: ImageUploadFieldProps) {
	const fileInputRef = useRef<HTMLInputElement>(null)
	const [uploading, setUploading] = useState(false)

	const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0]
		if (!file) return

		if (file.size > 5 * 1024 * 1024) {
			toast.error('文件大小不能超过5MB')
			return
		}

		if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
			toast.error('只支持 JPG/PNG 格式')
			return
		}

		setUploading(true)
		try {
			const url = await authApi.uploadImage(file)
			onChange(url)
		} catch {
			toast.error('图片上传失败')
		} finally {
			setUploading(false)
			if (fileInputRef.current) fileInputRef.current.value = ''
		}
	}

	const handleRemove = () => {
		onChange('')
	}

	return (
		<div className="space-y-2">
			<input
				ref={fileInputRef}
				type="file"
				accept="image/jpeg,image/png"
				className="hidden"
				onChange={handleFileChange}
			/>
			{value ? (
				<div className="relative inline-block">
					<img
						src={value}
						alt="preview"
						className="h-24 w-24 rounded-md border object-cover"
					/>
					<Button
						type="button"
						variant="destructive"
						size="icon"
						className="absolute -right-2 -top-2 h-5 w-5"
						onClick={handleRemove}
					>
						<X className="h-3 w-3" />
					</Button>
				</div>
			) : (
				<Button
					type="button"
					variant="outline"
					className="h-24 w-24 flex-col gap-1"
					onClick={() => fileInputRef.current?.click()}
					disabled={uploading}
				>
					{uploading ? (
						<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
					) : (
						<>
							<Upload className="h-5 w-5 text-muted-foreground" />
							<span className="text-xs text-muted-foreground">{placeholder}</span>
						</>
					)}
				</Button>
			)}
		</div>
	)
}
